import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, CheckCircle2, Clock, Stethoscope } from "lucide-react";
import { toast } from "sonner";

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function PublicBooking() {
  const slug = window.location.pathname.split("/").filter(Boolean)[1] || "beltran-medical-clinic";

  const [boot, setBoot] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    hospital_slug: slug,
    patient_name: "",
    patient_contact: "",
    patient_email: "",
    doctor_id: "",
    reservation_date: today(),
    reservation_time: "",
    reason: "",
    notes: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      const { data } = await api.get(`/public/booking/${slug}`);
      setBoot(data);
      const firstDoctor = data?.doctors?.[0]?.id || "";
      setForm((f) => ({ ...f, doctor_id: firstDoctor }));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const selectedDoctor = useMemo(
    () => (boot?.doctors || []).find((d) => d.id === form.doctor_id),
    [boot, form.doctor_id]
  );

  const selectedSchedule = selectedDoctor?.schedule || {};
  const effectiveSlots = selectedSchedule.slot_times?.length ? selectedSchedule.slot_times : (boot?.slots || []);

  const dateAllowed = useMemo(() => {
    if (!form.reservation_date || !selectedDoctor) return true;
    if ((selectedSchedule.blocked_dates || []).includes(form.reservation_date)) return false;

    const dayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const day = dayMap[new Date(form.reservation_date + "T00:00:00").getDay()];
    const activeDays = selectedSchedule.active_days || ["MON", "TUE", "WED", "THU", "FRI"];
    return activeDays.includes(day);
  }, [form.reservation_date, selectedDoctor, selectedSchedule]);

  const submit = async (e) => {
    e.preventDefault();

    if (!form.patient_name.trim() || !form.patient_contact.trim()) {
      toast.error("Patient name and contact number are required");
      return;
    }
    if (!form.reservation_date || !form.reservation_time) {
      toast.error("Choose date and time");
      return;
    }

    if (!dateAllowed) {
      toast.error("Selected doctor is not available on this date");
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post("/public/reservations", form);
      setDone(data);
      toast.success("Reservation submitted");
    } catch (e2) {
      toast.error(formatErr(e2?.response?.data?.detail || e2));
    } finally {
      setBusy(false);
    }
  };

  const hospital = boot?.hospital || {};

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-3xl bg-white shadow-xl p-8 text-center">
          <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
          <h1 className="text-3xl font-black mt-5 text-slate-900">Reservation Submitted</h1>
          <p className="text-slate-500 mt-3">{done.message}</p>
          <div className="rounded-2xl bg-blue-50 text-blue-800 mt-6 p-4 text-sm">
            Please wait for clinic confirmation. Keep your phone available.
          </div>
          <Button className="mt-6 w-full" onClick={() => window.location.href = "/login"}>
            Back to eMedbay
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="grid lg:grid-cols-[0.9fr_1.1fr] min-h-screen">
        <section className="bg-gradient-to-br from-blue-900 via-blue-700 to-cyan-400 text-white p-8 md:p-12 flex flex-col justify-between">
          <div>
            <div className="text-5xl font-black">eMedbay</div>
            <div className="tracking-[0.35em] text-xs mt-3 text-blue-100">APPOINTMENT BOOKING</div>
          </div>

          <div className="my-10">
            <h1 className="text-5xl font-black leading-tight">{hospital.name || "Clinic Appointment"}</h1>
            <p className="text-blue-50 text-lg mt-4">{hospital.address || "Book your clinic visit online."}</p>

            <div className="grid grid-cols-2 gap-3 mt-8">
              <div className="rounded-2xl bg-white/10 border border-white/15 p-4">
                <CalendarDays />
                <div className="text-2xl font-black mt-3">{form.reservation_date}</div>
                <div className="text-sm text-blue-100">Selected Date</div>
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/15 p-4">
                <Clock />
                <div className="text-2xl font-black mt-3">{form.reservation_time || "—"}</div>
                <div className="text-sm text-blue-100">Selected Time</div>
              </div>
            </div>
          </div>

          <div className="text-sm text-blue-100">No login required. Your request will be reviewed by the clinic.</div>
        </section>

        <section className="p-6 md:p-12 flex items-center">
          <form onSubmit={submit} className="w-full max-w-3xl mx-auto bg-white rounded-3xl shadow-xl p-6 md:p-8 space-y-5">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-blue-700 font-bold">Set Appointment</div>
              <h2 className="text-3xl font-black text-slate-900 mt-2">Choose your schedule</h2>
            </div>

            <div>
              <label className="text-sm font-bold text-slate-700">Doctor</label>
              <select value={form.doctor_id} onChange={(e) => set("doctor_id", e.target.value)} className="mt-1 w-full h-12 rounded-lg border border-slate-300 px-3">
                <option value="">Any available doctor</option>
                {(boot?.doctors || []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              {selectedDoctor ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 mt-2">
                  <Stethoscope size={14} /> {selectedDoctor.specialty || "Clinic Doctor"}
                </div>
              ) : null}
              {!dateAllowed ? (
                <div className="mt-2 rounded-lg bg-red-50 text-red-700 border border-red-200 p-2 text-sm">
                  This doctor is not available on the selected date.
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-slate-700">Date</label>
                <Input type="date" min={today()} value={form.reservation_date} onChange={(e) => set("reservation_date", e.target.value)} className="mt-1 h-12" />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700">Time</label>
                <select value={form.reservation_time} onChange={(e) => set("reservation_time", e.target.value)} className="mt-1 w-full h-12 rounded-lg border border-slate-300 px-3">
                  <option value="">Choose time</option>
                  {effectiveSlots.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="border-t pt-5">
              <h3 className="font-black text-slate-900 mb-3">Patient Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Patient full name" value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} className="h-12" />
                <Input placeholder="Contact number" value={form.patient_contact} onChange={(e) => set("patient_contact", e.target.value)} className="h-12" />
                <Input placeholder="Email optional" value={form.patient_email} onChange={(e) => set("patient_email", e.target.value)} className="h-12 md:col-span-2" />
              </div>
            </div>

            <Input placeholder="Reason for visit" value={form.reason} onChange={(e) => set("reason", e.target.value)} className="h-12" />

            <Button type="submit" disabled={busy} className="w-full h-14 text-lg bg-blue-700 hover:bg-blue-800">
              {busy ? "Submitting..." : "Submit Appointment Request"}
            </Button>
          </form>
        </section>
      </div>
    </div>
  );
}
